import { configs } from '@pec/aion-ui-i18next/constants';
import { Features } from '../types/enums';
import { FunctionFactory, ItemValue, Model, Question } from 'survey-core/survey.core';
import { round } from 'lodash';
import { SubscriptionTiers } from '@pec/aion-ui-core/enums/Subscription';

type ExpressionFunction = {
  question: Question;
  survey: Model;
};

interface IDiscount {
  id: string;
  amount: number;
  clientId?: string;
  appliesTo: string[];
}

export const currency = {
  AU: 'AUD',
  GB: 'GBP'
};

export const registerCustomFunctions = () => {
  FunctionFactory.Instance.register('expandChoicesByUrl', expandChoicesByUrl);
  FunctionFactory.Instance.register('getPrice', getPrice);
  FunctionFactory.Instance.register('formatPrice', formatPrice);
  FunctionFactory.Instance.register('getPriceWithDiscount', getPriceWithDiscount);
  FunctionFactory.Instance.register('getPriceTotal', getPriceTotal);
  FunctionFactory.Instance.register('getJoiningFee', getJoiningFee);
  FunctionFactory.Instance.register('showAdditionalProductName', showAdditionalProductName);
};

export function showAdditionalProductName(this: ExpressionFunction) {
  const { survey } = this;
  const subscriptionQuestion = survey.getQuestionByName('additionalProducts');
  const choices = getProductsChoices(survey);
  const selection = choices.find((item: ItemValue) => item?.value === subscriptionQuestion?.value);
  return selection?.originalItem?.purchaseType;
}

function getProductsChoices(survey: Model) {
  const subscriptionQuestion = survey.getQuestionByName('subscriptionSelect');

  return subscriptionQuestion.activeChoices.length > 0
    ? subscriptionQuestion.activeChoices // Prefer reading choices from the question
    : survey.getVariable('productsChoices') || [];
}

export function expandChoicesByUrl(this: ExpressionFunction, [findValue, property, findKey]: string[]) {
  const { survey } = this;
  const choices = getProductsChoices(survey);

  const choice = choices.find((item: ItemValue) =>
    findKey ? item.originalItem[findKey] === findValue : item.value === findValue
  );

  return choice && choice.originalItem && choice.originalItem[property];
}

export function getPrice(this: ExpressionFunction, [productId, customPrice]: [string, number | undefined]) {
  const { survey } = this;
  const choices = getProductsChoices(survey);

  const choice = choices.find((item: ItemValue) => item.value === productId);

  if (choice && choice.originalItem) {
    const { price } = choice.originalItem;
    const operatedPrice = customPrice ?? price;

    return operatedPrice;
  }
  return 0;
}

export function formatPrice(this: ExpressionFunction, [price]: number[]) {
  const { survey } = this;
  const choices = getProductsChoices(survey);

  return choices[0]
    ? new Intl.NumberFormat(choices[0].originalItem.location, {
        style: 'currency',
        currency: getCurrencyByLocation(choices[0].originalItem.location)
      }).format(price)
    : price;
}

export function getJoiningFee(this: ExpressionFunction, [key]: string[]) {
  const { survey } = this;
  const choices = getProductsChoices(survey);
  const joiningFee = choices.find((item: ItemValue) => item.originalItem.subscriptionTier === 'CHAS Joining Fee');

  return !joiningFee ? null : joiningFee.originalItem[key || 'price'];
}

export function getPriceWithDiscount(
  this: ExpressionFunction,
  [productId, applyOnlyByProductIdMatch]: [string, boolean | undefined]
) {
  const { survey } = this;
  const choices = getProductsChoices(survey);

  const choice = choices.find((item: ItemValue) => item.value === productId);

  if (choice && choice.originalItem) {
    const { price } = choice.originalItem;
    const discount = survey.getVariable('discount') as IDiscount | undefined;

    if (
      discount &&
      (discount.appliesTo.includes(productId) || (!applyOnlyByProductIdMatch && discount.appliesTo.length === 0))
    ) {
      return (price * 100 - round((price * discount.amount) / 100, 2) * 100) / 100;
    }
    return price;
  }
  return 0;
}

export function getPriceTotal(this: ExpressionFunction, [priceWithDiscount]: number[]) {
  const { survey } = this;
  const taxRate = survey.getVariable('taxRate');
  const taxPercent = taxRate?.isActive ? taxRate.ratePercentage : 0;
  return taxRate
    ? (priceWithDiscount * 100 + round((priceWithDiscount * taxPercent) / 100, 2) * 100) / 100
    : priceWithDiscount;
}

export const getLocation = (survey: Model) => {
  const choices = getProductsChoices(survey);
  return choices[0].originalItem.location;
};

export const getCurrencyByLocation = (location: string) => {
  return currency[location] || configs.currency.currency;
};

export function getTierName(text: string) {
  return text.replace(/CHAS/i, '').trim();
}

export function mapFeatures(feature: string, text: string) {
  const features = {
    [Features.SSIP]: [SubscriptionTiers.Elite, SubscriptionTiers.Advanced, SubscriptionTiers.Standard],
    [Features.PAS91]: [SubscriptionTiers.Elite, SubscriptionTiers.Advanced],
    [Features.PublicSector]: [SubscriptionTiers.Elite],
    [Features.CommonAssessment]: [SubscriptionTiers.Elite],
    [Features.IndustryWide]: [SubscriptionTiers.Elite]
  };

  return features[feature]?.includes(text);
}
